import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  FolderKanban,
  Loader2,
  Play,
  Save,
  Trash2,
} from 'lucide-react';
import { employeeApi, locationApi, myReportsApi, positionApi, scheduleApi } from '../services/api';
import type { EmployeePickerRow } from '../services/api';
import type {
  Location,
  MyReportRunDetail,
  MyReportRunListItem,
  MyReportTemplate,
  MyReportTypeDef,
  Position,
  Schedule,
} from '../types';
import ReportRunStatusBadge from '../components/ReportRunStatusBadge';
import BasketSelect from '../components/BasketSelect';
import { createCSVFile, createExcelFile } from '../utils/reportExportFiles';
import { formatCompanyDateTime } from '../utils/companyDateFormat';
import { appConfirm } from '../context/SystemDialogContext';
import { useLocationContext } from '../context/LocationContext';
import { usePermissions } from '../hooks/usePermissions';
import { canOpenCustomReportType } from '../permissions/permissionAccess';

function isoDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultFromDate() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return isoDate(d);
}

function defaultToDate() {
  return isoDate(new Date());
}

function parseIds(raw?: string | null): number[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function joinIds(ids: (number | string)[]): string {
  return ids.map(String).filter(Boolean).join(',');
}

function triggerFileDownload(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type Props = {
  templateId?: number | null;
  onTemplatesChanged?: () => void;
};

export default function MyReportsPage({ templateId = null, onTemplatesChanged }: Props) {
  const navigate = useNavigate();
  const { selectedLocationId } = useLocationContext();
  const [types, setTypes] = useState<MyReportTypeDef[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [employees, setEmployees] = useState<EmployeePickerRow[]>([]);
  const [runs, setRuns] = useState<MyReportRunListItem[]>([]);
  const { hasPermission } = usePermissions();
  const [reportType, setReportType] = useState('');
  const [name, setName] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [columns, setColumns] = useState<string[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [columnsExpanded, setColumnsExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMenu, setSavingMenu] = useState(false);
  const [viewing, setViewing] = useState<MyReportRunDetail | null>(null);
  const [loadingView, setLoadingView] = useState(false);
  const [loadedTemplate, setLoadedTemplate] = useState<MyReportTemplate | null>(null);
  const listRequestRef = useRef(0);
  const runLockRef = useRef(false);

  const allowedTypes = useMemo(
    () => types.filter((t) => canOpenCustomReportType(t.key, hasPermission)),
    [types, hasPermission],
  );
  const selected = useMemo(
    () => allowedTypes.find((t) => t.key === reportType) || null,
    [allowedTypes, reportType]
  );
  const isSaved = templateId != null && loadedTemplate?.id === templateId;

  const selectedLocationIds = parseIds(filters.locationIds);
  const locationItems = useMemo(() => {
    const items = locations.map((loc) => ({ id: loc.id, label: loc.name }));
    if (!selectedLocationId) return items;
    return [
      ...items.filter((item) => item.id === selectedLocationId),
      ...items.filter((item) => item.id !== selectedLocationId),
    ];
  }, [locations, selectedLocationId]);
  const scheduleItems = useMemo(() => {
    const active = schedules.filter((s) => s.isActive).map((s) => ({
      id: s.id,
      label: s.name,
      locationIds: s.locationIds || [],
    }));
    if (selectedLocationIds.length === 0) {
      return active.map(({ id, label }) => ({ id, label }));
    }
    return active
      .filter((s) => s.locationIds.length === 0 || s.locationIds.some((id) => selectedLocationIds.includes(id)))
      .map(({ id, label }) => ({ id, label }));
  }, [schedules, selectedLocationIds]);
  const positionItems = useMemo(
    () => positions.filter((p) => p.isActive).map((p) => ({ id: p.id, label: p.title })),
    [positions]
  );
  const employeeItems = useMemo(() => {
    return employees
      .filter((e) => {
        if (selectedLocationIds.length === 0) return true;
        if (selectedLocationIds.includes(e.mainLocationId)) return true;
        return (e.subLocationIds || []).some((id) => selectedLocationIds.includes(id));
      })
      .map((e) => ({
        id: e.id,
        label: `${e.firstName} ${e.lastName}`.trim(),
      }));
  }, [employees, selectedLocationIds]);

  const refreshRuns = useCallback(async () => {
    const requestId = ++listRequestRef.current;
    try {
      const list = await myReportsApi.list(templateId);
      if (requestId !== listRequestRef.current) return;
      setRuns(list || []);
    } catch {
      if (requestId !== listRequestRef.current) return;
    }
  }, [templateId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalog, locs, scheds, poss, emps] = await Promise.all([
          myReportsApi.catalog(),
          locationApi.getAll(),
          scheduleApi.getAll(),
          positionApi.getAll(),
          employeeApi.getPicker(),
        ]);
        if (cancelled) return;
        setTypes(catalog.types || []);
        setLocations(locs || []);
        setSchedules(scheds || []);
        setPositions(poss || []);
        setEmployees(emps || []);
      } catch (error) {
        console.error(error);
        alert('Failed to load My Reports catalog');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!allowedTypes.length) return;
    let cancelled = false;
    (async () => {
      if (templateId) {
        try {
          const saved = await myReportsApi.getTemplate(templateId);
          if (cancelled) return;
          if (!canOpenCustomReportType(saved.reportType, hasPermission)) {
            navigate('/reports', { replace: true });
            return;
          }
          applySaved(saved, allowedTypes);
        } catch (error) {
          console.error(error);
          if (!cancelled) {
            alert('Could not open this saved report.');
            navigate('/reports/my-reports', { replace: true });
          }
        }
      } else {
        setLoadedTemplate(null);
        if (allowedTypes[0]) applyType(allowedTypes[0]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, allowedTypes]);

  useEffect(() => {
    refreshRuns().catch(() => undefined);
  }, [refreshRuns]);

  useEffect(() => {
    const pending = runs.some((r) => /processing/i.test(r.status));
    if (!pending) return;
    const t = window.setInterval(() => {
      refreshRuns().catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(t);
  }, [runs, refreshRuns]);

  const applyType = (type: MyReportTypeDef) => {
    setReportType(type.key);
    setName(type.name);
    const nextFilters: Record<string, string> = {};
    type.filters.forEach((f) => {
      if (f.defaultValue) {
        nextFilters[f.key] = f.defaultValue;
      } else if (f.input === 'date' && f.key === 'fromDate') {
        nextFilters[f.key] = defaultFromDate();
      } else if (f.input === 'date' && f.key === 'toDate') {
        nextFilters[f.key] = defaultToDate();
      } else if (f.input === 'locations' && selectedLocationId) {
        nextFilters[f.key] = String(selectedLocationId);
      } else {
        nextFilters[f.key] = '';
      }
    });
    setFilters(nextFilters);
    setColumns(type.columns.filter((c) => c.defaultSelected).map((c) => c.key));
  };

  const applySaved = (saved: MyReportTemplate, catalog: MyReportTypeDef[]) => {
    const type = catalog.find((t) => t.key === saved.reportType) || catalog[0];
    if (!type) return;
    setLoadedTemplate(saved);
    setReportType(type.key);
    setName(saved.name);
    const nextFilters: Record<string, string> = {};
    type.filters.forEach((f) => {
      const stored =
        saved.filters?.[f.key] ||
        (f.key === 'locationIds' ? saved.filters?.locationId : undefined) ||
        (f.key === 'scheduleIds' ? saved.filters?.scheduleId : undefined) ||
        (f.key === 'positionIds' ? saved.filters?.positionId : undefined) ||
        (f.key === 'employeeIds' ? saved.filters?.employeeId : undefined);
      if (stored) {
        nextFilters[f.key] = stored;
      } else if (f.input === 'date' && f.key === 'fromDate') {
        nextFilters[f.key] = defaultFromDate();
      } else if (f.input === 'date' && f.key === 'toDate') {
        nextFilters[f.key] = defaultToDate();
      } else if (f.input === 'locations' && selectedLocationId) {
        nextFilters[f.key] = String(selectedLocationId);
      } else {
        nextFilters[f.key] = f.defaultValue || '';
      }
    });
    setFilters(nextFilters);
    const savedCols = (saved.columns || []).filter((k) => type.columns.some((c) => c.key === k));
    setColumns(
      savedCols.length > 0
        ? savedCols
        : type.columns.filter((c) => c.defaultSelected).map((c) => c.key)
    );
  };

  const definitionBody = () => ({
    reportType: selected!.key,
    name: name.trim() || selected!.name,
    filters,
    columns,
  });

  const handleSaveMenuItem = async () => {
    if (!selected) {
      alert('Select a report type.');
      return;
    }
    if (!name.trim()) {
      alert('Enter a report name. That name becomes the menu item under My Reports.');
      return;
    }
    if (columns.length === 0) {
      alert('Select at least one output column.');
      return;
    }
    setSavingMenu(true);
    try {
      if (isSaved && templateId) {
        const updated = await myReportsApi.updateTemplate(templateId, definitionBody());
        setLoadedTemplate(updated);
        onTemplatesChanged?.();
      } else {
        const created = await myReportsApi.createTemplate(definitionBody());
        setLoadedTemplate(created);
        onTemplatesChanged?.();
        navigate(`/reports/my-reports/${created.id}`);
      }
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to save this report to My Reports.');
    } finally {
      setSavingMenu(false);
    }
  };

  const handleDeleteMenuItem = async () => {
    if (!templateId || !isSaved) return;
    const label = loadedTemplate?.name || name || 'this report';
    if (
      !(await appConfirm(`Remove "${label}" from My Reports? Runs already generated are kept.`, {
        danger: true,
        confirmLabel: 'Remove',
      }))
    ) {
      return;
    }
    await myReportsApi.deleteTemplate(templateId);
    onTemplatesChanged?.();
    navigate('/reports/my-reports');
  };

  const handleRun = async () => {
    if (!selected) {
      alert('Select a report type.');
      return;
    }
    if (columns.length === 0) {
      alert('Select at least one output column.');
      return;
    }
    if (runLockRef.current) return;
    runLockRef.current = true;
    setSaving(true);
    try {
      await myReportsApi.create({
        ...definitionBody(),
        templateId: templateId || null,
      });
      await refreshRuns();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to start report.');
    } finally {
      runLockRef.current = false;
      setSaving(false);
    }
  };

  const openRun = async (id: number) => {
    setLoadingView(true);
    try {
      const detail = await myReportsApi.get(id);
      setViewing(detail);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Could not open this report.');
    } finally {
      setLoadingView(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await appConfirm('Delete this report run?', { danger: true, confirmLabel: 'Delete' }))) {
      return;
    }
    await myReportsApi.delete(id);
    if (viewing?.id === id) setViewing(null);
    await refreshRuns();
  };

  const exportRows = (detail: MyReportRunDetail, kind: 'csv' | 'xlsx') => {
    const headers = (detail.result?.headers || []).map((h) => h.label);
    const keys = (detail.result?.headers || []).map((h) => h.key);
    const dataRows = (detail.result?.rows || []).map((row) => keys.map((k) => row[k] || ''));
    const base = (detail.name || detail.reportTypeName || 'my-report').replace(/\s+/g, '-');
    const file = kind === 'csv' ? createCSVFile(headers, dataRows, base) : createExcelFile(headers, dataRows, base);
    triggerFileDownload(file.url, file.filename);
  };

  return (
    <div className="h-full overflow-auto p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-neutral-900 flex items-center gap-2">
          <FolderKanban className="h-6 w-6" />
          {isSaved ? loadedTemplate?.name || 'My Reports' : 'New report'}
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          {isSaved
            ? 'This report is saved under My Reports. Update filters or columns and save to keep the menu item, then run it when you need a new result.'
            : 'Choose a type, filters, and output columns, then save it under My Reports as a menu item. Only you can see reports you save.'}
        </p>
      </div>

      <section className="bg-white border border-neutral-200 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="font-medium text-neutral-700">Report type</span>
            <select
              value={reportType}
              onChange={(e) => {
                const next = allowedTypes.find((t) => t.key === e.target.value);
                if (next) {
                  applyType(next);
                  if (isSaved && loadedTemplate) setName(loadedTemplate.name);
                }
              }}
              className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md"
            >
              {allowedTypes.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-neutral-700">Menu item name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Shown under My Reports"
              className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </label>
        </div>
        {selected && <p className="text-sm text-neutral-600">{selected.description}</p>}

        <div className="flex flex-col gap-4">
          <div className="w-full border border-neutral-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setFiltersExpanded((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-neutral-50 hover:bg-neutral-100 text-left"
            >
              {filtersExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              <span className="font-semibold">Filters</span>
            </button>
            {filtersExpanded && selected && (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {selected.filters.map((filter) => {
                  if (filter.input === 'locations') {
                    return (
                      <div key={filter.key}>
                        <BasketSelect
                          label={filter.label}
                          availableItems={locationItems}
                          selectedIds={parseIds(filters[filter.key])}
                          onChange={(ids) => {
                            const nextLocs = ids.map(Number).filter((n) => Number.isFinite(n) && n > 0);
                            const allowedSchedules = new Set(
                              schedules
                                .filter(
                                  (s) =>
                                    nextLocs.length === 0 ||
                                    !s.locationIds?.length ||
                                    s.locationIds.some((id) => nextLocs.includes(id))
                                )
                                .map((s) => s.id)
                            );
                            const allowedEmployees = new Set(
                              employees
                                .filter((e) => {
                                  if (nextLocs.length === 0) return true;
                                  if (nextLocs.includes(e.mainLocationId)) return true;
                                  return (e.subLocationIds || []).some((id) => nextLocs.includes(id));
                                })
                                .map((e) => e.id)
                            );
                            setFilters((prev) => ({
                              ...prev,
                              [filter.key]: joinIds(ids),
                              scheduleIds: joinIds(
                                parseIds(prev.scheduleIds).filter((id) => allowedSchedules.has(id))
                              ),
                              employeeIds: joinIds(
                                parseIds(prev.employeeIds).filter((id) => allowedEmployees.has(id))
                              ),
                            }));
                          }}
                          placeholder="All locations"
                        />
                      </div>
                    );
                  }
                  if (filter.input === 'schedules') {
                    return (
                      <div key={filter.key}>
                        <BasketSelect
                          label={filter.label}
                          availableItems={scheduleItems}
                          selectedIds={parseIds(filters[filter.key])}
                          onChange={(ids) =>
                            setFilters((prev) => ({ ...prev, [filter.key]: joinIds(ids) }))
                          }
                          placeholder="All schedules"
                        />
                      </div>
                    );
                  }
                  if (filter.input === 'positions') {
                    return (
                      <div key={filter.key}>
                        <BasketSelect
                          label={filter.label}
                          availableItems={positionItems}
                          selectedIds={parseIds(filters[filter.key])}
                          onChange={(ids) =>
                            setFilters((prev) => ({ ...prev, [filter.key]: joinIds(ids) }))
                          }
                          placeholder="All positions"
                        />
                      </div>
                    );
                  }
                  if (filter.input === 'employees') {
                    return (
                      <div key={filter.key}>
                        <BasketSelect
                          label={filter.label}
                          availableItems={employeeItems}
                          selectedIds={parseIds(filters[filter.key])}
                          onChange={(ids) =>
                            setFilters((prev) => ({ ...prev, [filter.key]: joinIds(ids) }))
                          }
                          placeholder="All employees"
                        />
                      </div>
                    );
                  }
                  return (
                  <label key={filter.key} className="block text-sm">
                    <span className="text-neutral-700">{filter.label}</span>
                    {filter.input === 'select' ? (
                      <select
                        value={filters[filter.key] || ''}
                        onChange={(e) => setFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md"
                      >
                        {(filter.options || []).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : filter.input === 'location' ? (
                      <select
                        value={filters[filter.key] || ''}
                        onChange={(e) => setFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md"
                      >
                        <option value="">All locations</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={String(loc.id)}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={filter.input === 'date' ? 'date' : filter.input === 'number' ? 'number' : 'text'}
                        value={filters[filter.key] || ''}
                        onChange={(e) => setFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md"
                      />
                    )}
                  </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="w-full border border-neutral-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setColumnsExpanded((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-neutral-50 hover:bg-neutral-100 text-left"
            >
              {columnsExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              <span className="font-semibold">Output columns</span>
            </button>
            {columnsExpanded && selected && (
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-2">
                {selected.columns.map((col) => {
                  const checked = columns.includes(col.key);
                  return (
                    <label key={col.key} className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setColumns((prev) =>
                            checked ? prev.filter((k) => k !== col.key) : [...prev, col.key]
                          )
                        }
                      />
                      {col.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleSaveMenuItem}
            disabled={savingMenu || !selected}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {savingMenu ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isSaved ? 'Save menu item' : 'Save as menu item'}
          </button>
          {isSaved && (
            <button
              type="button"
              onClick={handleDeleteMenuItem}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-red-200 text-red-700 text-sm font-medium rounded-md hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Remove from menu
            </button>
          )}
          <button
            type="button"
            onClick={handleRun}
            disabled={saving || !selected}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-neutral-900 text-white text-sm font-medium rounded-md hover:bg-neutral-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run report
          </button>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-200">
          <h2 className="font-semibold text-neutral-900">{isSaved ? 'Runs of this report' : 'My runs'}</h2>
          <p className="text-xs text-neutral-500">Visible only to you.</p>
        </div>
        {runs.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500">
            {isSaved ? 'No runs of this saved report yet.' : 'No reports yet. Save a menu item or run one.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Rows</th>
                  <th className="p-3">Created</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="border-t border-neutral-100">
                    <td className="p-3 font-medium">{run.name || run.reportTypeName}</td>
                    <td className="p-3">{run.reportTypeName}</td>
                    <td className="p-3">
                      <ReportRunStatusBadge status={run.status as any} />
                    </td>
                    <td className="p-3">{run.rowCount}</td>
                    <td className="p-3 whitespace-nowrap">{formatCompanyDateTime(run.createdAt)}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        title="View"
                        disabled={!/readyforview/i.test(run.status) || loadingView}
                        onClick={() => openRun(run.id)}
                        className="p-2 text-neutral-600 hover:text-blue-600 disabled:opacity-30"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => handleDelete(run.id)}
                        className="p-2 text-neutral-600 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <h3 className="font-semibold">{viewing.name || viewing.reportTypeName}</h3>
                <p className="text-xs text-neutral-500">{viewing.rowCount} rows</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm border rounded-md"
                  onClick={() => exportRows(viewing, 'csv')}
                >
                  CSV
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm border rounded-md"
                  onClick={() => exportRows(viewing, 'xlsx')}
                >
                  Excel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm border rounded-md"
                  onClick={() => setViewing(null)}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="overflow-auto p-4">
              {!viewing.result ? (
                <p className="text-sm text-neutral-500">No result yet.</p>
              ) : (
                <table className="min-w-full text-sm border">
                  <thead className="bg-neutral-50">
                    <tr>
                      {viewing.result.headers.map((h) => (
                        <th key={h.key} className="text-left p-2 border-b whitespace-nowrap">
                          {h.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {viewing.result.rows.map((row, idx) => (
                      <tr key={idx} className="border-b border-neutral-100">
                        {viewing.result!.headers.map((h) => (
                          <td key={h.key} className="p-2 whitespace-nowrap">
                            {row[h.key] || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
